/**
 * Global `window.fetch` wrapper that auto-injects the Super Admin
 * impersonation header on every same-origin `/api/*` request.
 *
 * The codebase has ~50 raw `fetch('/api/...')` call sites that bypass
 * `apiClient` (and therefore the header injection in `lib/api.ts`).
 * Wrapping `window.fetch` once at app boot catches all of them — current
 * and future — without per-call-site changes.
 *
 * Safe by design:
 * - Only acts when impersonation is active (sessionStorage state present).
 * - Only touches same-origin `/api/*` URLs; pass-through for everything else.
 * - Never overwrites an `X-Impersonate-User-Id` header the caller set itself.
 * - Idempotent install (StrictMode double-mount safe).
 */
import { getImpersonatedUserId } from './impersonation';

const IMPERSONATION_HEADER = 'X-Impersonate-User-Id';
let installed = false;

function getRequestUrl(input: RequestInfo | URL): string | null {
  try {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    // Request
    return (input as Request).url;
  } catch {
    return null;
  }
}

function isSameOriginApiUrl(urlStr: string): boolean {
  // Fast path: relative URL starting with /api/
  if (urlStr.startsWith('/api/')) return true;
  // Absolute URL: must share origin and path under /api/
  try {
    const u = new URL(urlStr, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

export function installImpersonationFetch(): void {
  if (installed) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const originalFetch: typeof window.fetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const impersonatedUserId = getImpersonatedUserId();
    if (!impersonatedUserId) {
      return originalFetch(input, init);
    }

    const url = getRequestUrl(input);
    if (!url || !isSameOriginApiUrl(url)) {
      return originalFetch(input, init);
    }

    // Build a Headers object from whatever shape the caller passed.
    let headers: Headers;
    if (init?.headers) {
      headers = new Headers(init.headers);
    } else if (input instanceof Request) {
      headers = new Headers(input.headers);
    } else {
      headers = new Headers();
    }

    // Preserve any explicit override the caller set.
    if (!headers.has(IMPERSONATION_HEADER)) {
      headers.set(IMPERSONATION_HEADER, String(impersonatedUserId));
    }

    return originalFetch(input, { ...(init || {}), headers });
  };
}
